import { useState, type ReactNode } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { ArrowRight, Menu, X } from 'lucide-react'

import { Logo } from '@/components/logo'
import { Button } from '@/components/ui/button'
import { ThemeToggle } from '@/components/ui/theme-toggle'
import { useSession } from '@/features/auth/api'
import { useHeaderHide } from '@/lib/motion'
import { cn } from '@/lib/cn'

const navigation = [
  { to: '/kelas', label: 'Kelas' },
  { to: '/jasa', label: 'Jasa' },
  { to: '/proyek', label: 'Proyek' },
  { to: '/sayembara', label: 'Sayembara' },
  { to: '/lowongan', label: 'Lowongan' },
  { to: '/talenta', label: 'Talenta' },
  { to: '/panduan', label: 'Panduan' },
]

/** Kerangka halaman publik: beranda, katalog, dan halaman kelas. */
export function PublicShell({ children }: { children: ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const { data: session } = useSession()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const headerHidden = useHeaderHide()

  return (
    <div className="flex min-h-full flex-col bg-surface">
      {/* Tautan lewati navigasi adalah syarat pertama navigasi papan ketik
          yang layak; ia hanya muncul saat difokuskan. */}
      <a
        href="#konten"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-brand focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white"
      >
        Lewati ke konten utama
      </a>

      {/* Bilah navigasi melayang sebagai satu pil, tidak menempel pada tepi
          layar. Bentuk pil ini yang membuatnya terbaca sebagai objek di atas
          blok warna hero, bukan sebagai bingkai halaman. */}
      <header
        className={cn(
          'sticky top-0 z-40 px-3 pt-3 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] sm:px-5 sm:pt-5',
          // Menggulir turun berarti pengguna sedang membaca, bukan sedang
          // mencari menu; bilahnya menyingkir dan kembali begitu ia menggulir
          // naik. Menu yang sedang terbuka menahannya tetap terlihat.
          headerHidden && !menuOpen && '-translate-y-[130%]',
        )}
      >
        <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between gap-4 rounded-2xl border border-line bg-surface/90 px-4 backdrop-blur-xl sm:px-5">
          <div className="flex items-center gap-8">
            <Link to="/" aria-label="Beranda Skava">
              <Logo />
            </Link>

            <nav className="hidden items-center gap-1 lg:flex" aria-label="Navigasi utama">
              {navigation.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    'mono-label rounded-full px-3 py-2 text-[11px] transition',
                    pathname.startsWith(item.to)
                      ? 'bg-inverted text-on-inverted'
                      : 'text-muted hover:text-ink',
                  )}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-2">
            <ThemeToggle className="hidden sm:inline-flex" />

            {session ? (
              <Link to="/app">
                <Button size="sm">Dasbor saya</Button>
              </Link>
            ) : (
              <>
                <Link to="/masuk" className="hidden sm:block">
                  <Button variant="ghost" size="sm">
                    Masuk
                  </Button>
                </Link>
                <Link to="/daftar">
                  <Button size="sm">
                    Daftar
                    <ArrowRight className="size-3.5" />
                  </Button>
                </Link>
              </>
            )}

            <button
              type="button"
              className="inline-flex size-10 items-center justify-center rounded-full border border-line text-ink lg:hidden"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-label="Buka menu navigasi"
            >
              {menuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
            </button>
          </div>
        </div>

        {menuOpen ? (
          <nav
            className="mx-auto mt-2 w-full max-w-7xl rounded-3xl border border-line bg-surface px-4 py-3 shadow-[var(--shadow-lifted)] lg:hidden"
            aria-label="Navigasi seluler"
          >
            <ul className="space-y-1">
              {navigation.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={() => setMenuOpen(false)}
                    className="mono-label block rounded-full px-3 py-2.5 text-xs text-ink hover:bg-raised"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
              {!session ? (
                <li>
                  <Link
                    to="/masuk"
                    onClick={() => setMenuOpen(false)}
                    className="mono-label block rounded-full px-3 py-2.5 text-xs text-ink hover:bg-raised"
                  >
                    Masuk
                  </Link>
                </li>
              ) : null}
              {/* Pengalih tema disembunyikan dari bilah atas pada layar sempit,
                  jadi tempatnya di sini agar tetap dapat dijangkau. */}
              <li className="flex items-center justify-between rounded-lg px-3 py-2">
                <span className="text-sm font-semibold text-ink">Tema tampilan</span>
                <ThemeToggle />
              </li>
            </ul>
          </nav>
        ) : null}
      </header>

      <main id="konten" className="flex-1">
        {children}
      </main>

      <SiteFooter />
    </div>
  )
}

function SiteFooter() {
  const columns = [
    {
      title: 'Belajar',
      links: [
        { to: '/kelas', label: 'Katalog kelas' },
        { to: '/talenta', label: 'Direktori talenta' },
      ],
    },
    {
      title: 'Bekerja',
      links: [
        { to: '/jasa', label: 'Paket jasa' },
        { to: '/proyek', label: 'Papan proyek' },
        { to: '/sayembara', label: 'Sayembara' },
      ],
    },
    {
      title: 'Karier',
      links: [
        { to: '/lowongan', label: 'Lowongan kerja' },
        { to: '/panduan', label: 'Panduan pemakaian' },
        { to: '/daftar', label: 'Buat akun' },
      ],
    },
  ]

  return (
    <footer className="border-t border-line bg-surface">
      <div className="mx-auto grid w-full max-w-7xl gap-10 px-4 py-12 sm:px-6 md:grid-cols-[2fr_1fr_1fr_1fr]">
        <div>
          <Logo />
          <p className="mt-3 max-w-sm text-sm leading-relaxed text-muted">
            Kelas dari praktisi, paket jasa siap pesan, proyek escrow, sayembara, dan lowongan kerja. Dana
            pekerjaan dikunci di rekening penampungan dan hanya dilepas ketika hasilnya disetujui.
          </p>
        </div>

        {columns.map((column) => (
          <div key={column.title}>
            <h3 className="mono-label text-[11px] text-subtle">{column.title}</h3>
            <ul className="mt-1.5">
              {column.links.map((link) => (
                <li key={`${column.title}-${link.label}`}>
                  <Link
                    to={link.to}
                    className="inline-block py-2.5 text-sm text-muted transition hover:text-ink"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-line">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-2 px-4 py-5 text-xs text-subtle sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <p>© {new Date().getFullYear()} skava.id. Seluruh nominal ditampilkan dalam Rupiah.</p>
          <p>Layanan penampungan dana dijalankan bersama mitra penyelenggara jasa pembayaran berlisensi.</p>
        </div>
      </div>
    </footer>
  )
}
